export const TRIAL_MANAGER_URL = "https://t.me/MMC_Manager";
export const MIN_BALANCE_FOR_ACCESS = 10;

// The trial is no longer time-limited — it's metered by usage. Mirrors
// TRIAL_SIGNAL_LIMIT / TRIAL_SIGNAL_TIMEFRAMES in the backend's
// users/models.py: every trial account gets this many signals in each
// (section, deal_type) bucket below. "signals" is split by timeframe;
// "scanner" has no timeframe concept in its request, so it's a single
// flat bucket keyed by "". The backend is the actual source of truth
// for both the running counts (trial_signals_remaining, read via
// getTrialSignalsRemaining below) and whether the trial as a whole is
// still usable (trial_active) — nothing here is enforced client-side.
export const TRIAL_SIGNAL_LIMIT = 5;
export const TRIAL_SIGNAL_BUCKETS = {
  signals: ["M15", "M30", "H1", "H4", "15m", "30m", "1h", "4h", "S5", "M2", "M5", "M10"],
  scanner: [""],
};

export function getSectionAccess(user) {
  if (user?.is_admin === true) {
    const sections = new Set(user?.accessible_sections || []);
    sections.add("profile");
    return { sections, lockReason: "access" };
  }

  const isTrial = user?.is_trial === true;
  // The backend computes trial_active itself, based on whether any
  // bucket still has quota left — we just read the flag it hands us.
  const trialActive = isTrial && user?.trial_active === true;

  // Active trial users get normal section access even with balance = 0.
  if (isTrial && trialActive) {
    const sections = new Set(user?.accessible_sections || []);
    sections.add("profile");
    return { sections, lockReason: "access" };
  }

  // Once every bucket's trial quota is used up, keep the user/account
  // but lock all product sections. Profile remains available so the
  // user can complete account setup.
  if (isTrial && !trialActive) {
    return { sections: new Set(["profile"]), lockReason: "trial_expired" };
  }

  // Existing balance gate for regular users.
  const balance = typeof user?.balance === "number" ? user.balance : 0;
  if (balance < MIN_BALANCE_FOR_ACCESS) {
    return { sections: new Set(["profile"]), lockReason: "balance" };
  }

  const sections = new Set(user?.accessible_sections || []);
  sections.add("profile");
  return { sections, lockReason: "access" };
}

/* Raw { section: { dealType: remaining } } snapshot the backend attaches
   to the user object — e.g. { signals: { S5: 5, M2: 3, M5: 0, M10: 5 },
   scanner: { "": 2 } }. Degrades to an empty object rather than
   throwing on missing/malformed data, since this is display-only. */
export function getTrialSignalsSnapshot(user) {
  const raw = user?.trial_signals_remaining;
  return raw && typeof raw === "object" ? raw : {};
}

/* Remaining count for one bucket, or null if the backend hasn't sent
   data for it (e.g. not a trial user, or an unrecognized section). */
export function getTrialSignalsRemaining(user, section, dealType = "") {
  const bucket = getTrialSignalsSnapshot(user)[section];
  const value = bucket?.[dealType];
  return typeof value === "number" ? value : null;
}
